import { useRef } from 'react'
import {
  ControllerProps,
  useController,
  FieldValues,
  FieldPath,
  useFormContext,
} from 'react-hook-form'
import useStableCallback from '../../../hooks/shared/useStableCallback'

type ArrayToggleControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = ControllerProps<TFieldValues, TName> & {
  getNextValue?: (
    currentValue: Array<string>,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Array<string>
}

const ArrayToggleController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: ArrayToggleControllerProps<TFieldValues, TName>,
) => {
  const {
    name,
    control: controlProp,
    rules,
    shouldUnregister,
    defaultValue,
    render,
    getNextValue,
  } = props
  const form = useFormContext<TFieldValues>()
  const control = controlProp ?? form.control
  const { field, fieldState, formState } = useController<TFieldValues, TName>({
    name,
    control,
    rules,
    shouldUnregister,
    defaultValue,
  })
  const stableOnChangeWrapped = useStableCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldValue = field.value as Array<string>
    if (getNextValue) {
      field.onChange(getNextValue(fieldValue, event))
      return
    }
    const indexToRemove = fieldValue.indexOf(event.target.value)
    if (indexToRemove > -1) {
      field.onChange([
        ...fieldValue.slice(0, indexToRemove),
        ...fieldValue.slice(indexToRemove + 1),
      ])
    } else {
      field.onChange([...fieldValue, event.target.value])
    }
  })
  const fieldRef = useRef({} as typeof field)
  fieldRef.current.name = field.name
  fieldRef.current.onBlur = field.onBlur
  fieldRef.current.ref = field.ref
  fieldRef.current.value = field.value
  fieldRef.current.onChange = stableOnChangeWrapped
  return render({
    field: fieldRef.current,
    fieldState,
    formState,
  })
}

export default ArrayToggleController
